import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import ConfirmationModal from '../../ConfirmationModal';
import { ActivityType, ConfirmationType, ItemType, WithdrawalType } from 'store/enums/BallotReviewTypes';
import { Reject } from 'store/types/Meeting';
import { defaultSnackbarErrorProps } from 'util/layout';
import { createImagesUploadHandler, useSafeSelectIdValue, withdrawalTypeLabel } from 'util/meeting';
import { defaultInputProps, defaultTinyMCEProps } from 'util/form';

import commonStyles from 'styles/common.module.scss';

const getRejectOptions = (rejects: Reject[]) =>
  rejects.map((r) => (
    <MenuItem key={r.id} value={r.id}>
      {`Reject Affiliate: ${r.votingInterest} - Reject Name: ${r.rejectedBy} - Negative #: ${r.number}`}
    </MenuItem>
  ));

const idLabel = 'reject';

const RejectSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity, currentDocument, rejects, setCurrentReject, saveReject, deleteReject } =
    useMeetingStaffContext();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRejectId, setSelectedRejectId] = useState('');
  const [draftRejectId, setDraftRejectId] = useState(() => `${idLabel}-${Date.now()}`);
  const [votingInterest, setVotingInterest] = useState('');
  const [rejectedBy, setRejectedBy] = useState('');
  const [number, setNumber] = useState('');
  const [reference, setReference] = useState('');
  const [text, setText] = useState('');
  const [taskForceInput, setTaskForceInput] = useState('');
  const [withdrawal, setWithdrawal] = useState<WithdrawalType | undefined>();
  const [withdrawalDate, setWithdrawalDate] = useState<string | null>(null);

  const rejectOptions = useMemo(() => getRejectOptions(rejects), [rejects]);

  const safeSelectedRejectId = useSafeSelectIdValue(selectedRejectId, rejects);

  const currentReject: Reject = useMemo(() => {
    return {
      id: selectedRejectId || draftRejectId,
      documentId: currentDocument?.id || '',
      votingInterest,
      rejectedBy,
      number,
      reference,
      text,
      taskForceInput,
      withdrawal,
      withdrawalDate,
    };
  }, [
    currentDocument?.id,
    draftRejectId,
    number,
    reference,
    rejectedBy,
    selectedRejectId,
    taskForceInput,
    text,
    votingInterest,
    withdrawal,
    withdrawalDate,
  ]);

  const handleRejectChange = useCallback((event) => {
    const rejectId = event.target.value;

    setSelectedRejectId(rejectId);
    if (!rejectId) {
      setDraftRejectId(`${idLabel}-${Date.now()}`);
    }
  }, []);

  const handleSaveReject = useCallback(() => {
    saveReject?.().then((saved) => {
      if (saved) {
        enqueueSnackbar(`Reject saved`, { variant: 'success' });
        setSelectedRejectId(saved.id);
        setDraftRejectId(`${idLabel}-${Date.now()}`);
      }
    });
  }, [enqueueSnackbar, saveReject]);

  const handleDeleteReject = useCallback(() => {
    if (!selectedRejectId) {
      enqueueSnackbar(`No reject selected to delete`, { variant: 'warning' });
      return;
    }

    deleteReject?.(selectedRejectId);
  }, [deleteReject, enqueueSnackbar, selectedRejectId]);

  const handleDisplayReject = useCallback(() => {
    if (!meetingSession?.currentDocument?.id) {
      enqueueSnackbar(`Document must be displayed first`, { variant: 'warning' });
      return;
    }

    saveReject?.().then((saved) => {
      if (!saved) return;

      setSelectedRejectId(saved.id);
      setDraftRejectId(`${idLabel}-${Date.now()}`);

      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentReject: saved,
        currentTechnicalIssue: undefined,
        currentComment: undefined,
      })
        .then(() => {
          logActivity?.({
            type: ActivityType.Reject,
            document: meetingSession.currentDocument,
            reject: saved,
          });

          enqueueSnackbar(`Displayed reject`, { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(`Failed to display reject`, defaultSnackbarErrorProps);
          console.error('Error displaying reject:', error);
        });
    });
  }, [enqueueSnackbar, logActivity, meetingId, meetingSession, saveReject]);

  const handleClearReject = useCallback(() => {
    setSelectedRejectId('');
    setDraftRejectId(`${idLabel}-${Date.now()}`);
    setVotingInterest('');
    setRejectedBy('');
    setNumber('');
    setReference('');
    setText('');
    setTaskForceInput('');
    setWithdrawal(undefined);
    setWithdrawalDate(null);

    MeetingService.saveMeetingSession({
      ...meetingSession,
      meetingId: meetingId,
      currentReject: undefined,
      currentTechnicalIssue: undefined,
      currentComment: undefined,
    })
      .then(() => {
        enqueueSnackbar(`Cleared reject`, { variant: 'success' });
      })
      .catch((error: string) => {
        enqueueSnackbar(`Failed to display reject`, defaultSnackbarErrorProps);
        console.error('Error displaying reject:', error);
      });
  }, [enqueueSnackbar, meetingId, meetingSession]);

  const handleSaveClick = useCallback(() => {
    if (selectedRejectId) {
      setSaveModalOpen(true);
      return;
    }
    handleSaveReject();
  }, [handleSaveReject, selectedRejectId]);

  const handleSaveModalSubmit = useCallback(() => {
    setSaveModalOpen(false);
    handleSaveReject();
  }, [handleSaveReject]);

  const handleDeleteClick = useCallback(() => {
    if (selectedRejectId) {
      setDeleteModalOpen(true);
      return;
    }
    handleDeleteReject();
  }, [handleDeleteReject, selectedRejectId]);

  const handleDeleteModalSubmit = useCallback(() => {
    setDeleteModalOpen(false);
    handleDeleteReject();
  }, [handleDeleteReject]);

  useEffect(() => {
    const reject = rejects.find((d) => d.id === selectedRejectId);

    if (reject) {
      setVotingInterest(reject?.votingInterest || '');
      setRejectedBy(reject?.rejectedBy || '');
      setNumber(reject?.number || '');
      setReference(reject?.reference || '');
      setText(reject?.text || '');
      setTaskForceInput(reject?.taskForceInput || '');
      setWithdrawal(reject?.withdrawal);
      setWithdrawalDate(reject?.withdrawalDate || null);
    }
  }, [rejects, selectedRejectId]);

  useEffect(() => {
    if (withdrawal !== WithdrawalType.WithdrawalMade) {
      setWithdrawalDate(null);
    }
  }, [withdrawal]);

  useEffect(() => {
    setCurrentReject?.(currentReject);
  }, [currentReject, setCurrentReject]);

  return (
    <>
      {' '}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <GridContainer>
          <GridItem md={6}>
            <TextField label={'Load Reject'} select value={safeSelectedRejectId} onChange={handleRejectChange}>
              <MenuItem value={''}>-- New Reject --</MenuItem>
              {rejectOptions}
            </TextField>
          </GridItem>
          <GridItem md={6} className={commonStyles.buttonGroup}>
            <Button variant="contained" color="primary" onClick={handleSaveClick}>
              Save
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteClick}>
              Delete
            </Button>
          </GridItem>
          <GridItem md={6}>
            <TextField
              label={'Voting Interest'}
              value={votingInterest}
              required
              onChange={(event) => {
                setVotingInterest(event.target.value);
              }}
            ></TextField>
          </GridItem>
          <GridItem md={6}>
            <TextField
              label={'Reject (Name & Company)'}
              value={rejectedBy}
              required
              onChange={(event) => {
                setRejectedBy(event.target.value);
              }}
            ></TextField>
          </GridItem>
          <GridItem md={6}>
            <TextField
              label={'Negative Number'}
              value={number}
              required
              onChange={(event) => {
                setNumber(event.target.value);
              }}
            ></TextField>
          </GridItem>
          <GridItem>
            <TextField
              label={'Negative Referenced Section/Paragraph'}
              value={reference}
              required
              multiline
              onChange={(event) => {
                setReference(event.target.value);
              }}
            ></TextField>
          </GridItem>
          <GridItem>
            <FormControl className="w-100">
              <FormLabel>Negative Text *</FormLabel>
              <Editor
                {...defaultTinyMCEProps}
                id="reject-text-editor"
                value={text}
                onEditorChange={(newValue: string) => setText(newValue)}
                init={{
                  ...defaultTinyMCEProps.init,
                  images_upload_handler: createImagesUploadHandler(meetingId),
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <TextField
              label={'TF input '}
              value={taskForceInput}
              multiline
              onChange={(event) => {
                setTaskForceInput(event.target.value);
              }}
            ></TextField>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>Withdrawal</FormLabel>
              <RadioGroup
                name="withdrawal-radio-group"
                value={withdrawal ?? ''}
                onChange={(event) => setWithdrawal(event.target.value as WithdrawalType)}
              >
                <FormControlLabel
                  value={WithdrawalType.NoWithdrawal}
                  control={<Radio />}
                  label={withdrawalTypeLabel[WithdrawalType.NoWithdrawal]}
                />
                <FormControlLabel
                  value={WithdrawalType.WithdrawalMade}
                  control={<Radio />}
                  label={withdrawalTypeLabel[WithdrawalType.WithdrawalMade]}
                />
              </RadioGroup>
            </FormControl>
          </GridItem>
          {withdrawal === WithdrawalType.WithdrawalMade && (
            <GridItem md={6}>
              <DatePicker
                value={withdrawalDate}
                onChange={(newValue) => setWithdrawalDate(newValue)}
                label={'Withdrawal Date'}
                renderInput={(params: TextFieldProps) => <TextField {...params} {...defaultInputProps} />}
              />
            </GridItem>
          )}
          <GridItem className={commonStyles.buttonGroup}>
            <Button variant="contained" color="secondary" onClick={handleDisplayReject}>
              Display Reject
            </Button>
            <Button variant="contained" color="error" onClick={handleClearReject}>
              Clear Reject
            </Button>
          </GridItem>
        </GridContainer>
      </LocalizationProvider>
      <ConfirmationModal
        itemType={ItemType.Reject}
        confirmationType={ConfirmationType.Overwrite}
        open={saveModalOpen}
        onSubmit={handleSaveModalSubmit}
        onClose={() => setSaveModalOpen(false)}
      />
      <ConfirmationModal
        itemType={ItemType.Reject}
        confirmationType={ConfirmationType.Delete}
        open={deleteModalOpen}
        onSubmit={handleDeleteModalSubmit}
        onClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default RejectSection;
